import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import CotaCard, { buildWhatsAppLink } from "./CotaCard";
import { AlertTriangle, FileText, FileSpreadsheet, Filter, Share2, Calculator, Loader2, Search as SearchIcon, ArrowUpDown, Copy, X } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { Slider } from "./ui/slider";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter,
} from "./ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";

const TITLES = {
  imovel: "Cartas contempladas para imóvel",
  veiculo: "Cartas contempladas para veículo",
  todos: "Todas as cartas contempladas",
};

const money = (v) => "R$ " + v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Row({ label, value, accent, bold }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-400">{label}:</span>
      <span className={`text-right ${bold ? "font-bold" : "font-semibold"}`} style={accent ? { color: accent } : { color: "#334155" }}>
        {value}
      </span>
    </div>
  );
}

export default function Listing({ segmento }) {
  const [base, setBase] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/cotas`, { params: { segmento } }),
      axios.get(`${API}/administradoras`, { params: { segmento } }),
    ])
      .then(([c, a]) => {
        if (!active) return;
        setBase(c.data || []);
        setAdmins(a.data || []);
      })
      .catch(() => active && setBase([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [segmento]);

  const maxCredito = useMemo(() => Math.max(...base.map((c) => c.credito), 100000), [base]);

  const [selected, setSelected] = useState({});
  const [detail, setDetail] = useState(null);
  const [somarOpen, setSomarOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [admFilter, setAdmFilter] = useState("all");
  const [range, setRange] = useState([0, maxCredito]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("credito_desc");

  useEffect(() => {
    setSelected({});
    setAdmFilter("all");
    setRange([0, maxCredito]);
    setSearch("");
  }, [segmento, maxCredito]);

  // "Oportunidade" = a única melhor cota (menor relação entrada/crédito).
  const opportunityCodes = useMemo(() => {
    const valid = base.filter((c) => c.credito > 0);
    if (valid.length === 0) return new Set();
    const best = [...valid].sort((a, b) => a.entrada / a.credito - b.entrada / b.credito)[0];
    return new Set([best.codigo]);
  }, [base]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = base.filter(
      (c) =>
        (admFilter === "all" || c.administradora === admFilter) &&
        c.credito >= range[0] &&
        c.credito <= range[1] &&
        (q === "" || c.codigo.toLowerCase().includes(q))
    );
    const parc = (c) => parseInt(String(c.parcelas).replace(/\D/g, ""), 10) || 0;
    const cmp = {
      credito_desc: (a, b) => b.credito - a.credito,
      credito_asc: (a, b) => a.credito - b.credito,
      entrada_desc: (a, b) => b.entrada - a.entrada,
      entrada_asc: (a, b) => a.entrada - b.entrada,
      parcelas_desc: (a, b) => parc(b) - parc(a),
      parcelas_asc: (a, b) => parc(a) - parc(b),
    }[sort];
    return [...list].sort(cmp);
  }, [base, admFilter, range, search, sort]);

  const selectedCotas = base.filter((c) => selected[c.codigo]);
  const allSelected = filtered.length > 0 && filtered.every((c) => selected[c.codigo]);
  const selectedAdmin = selectedCotas[0]?.administradora || null;

  const toggle = (codigo) => {
    const cota = base.find((c) => c.codigo === codigo);
    setSelected((s) => {
      const isSelecting = !s[codigo];
      if (isSelecting) {
        const current = base.filter((c) => s[c.codigo]);
        if (current.length > 0 && current[0].administradora !== cota.administradora) {
          toast.error(`Só é possível somar cotas da mesma administradora (${current[0].administradora}).`);
          return s;
        }
      }
      return { ...s, [codigo]: !s[codigo] };
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      const n = { ...selected };
      filtered.forEach((c) => delete n[c.codigo]);
      setSelected(n);
      return;
    }
    const admins = new Set(filtered.map((c) => c.administradora));
    if (admins.size > 1) {
      toast.error("Filtre por uma administradora para selecionar todas as cotas.");
      return;
    }
    const n = { ...selected };
    filtered.forEach((c) => (n[c.codigo] = true));
    setSelected(n);
  };

  const clearSelection = () => setSelected({});

  const sum = (f) => selectedCotas.reduce((a, c) => a + (c[f] || 0), 0);
  const totalCredito = sum("credito");
  const totalEntrada = sum("entrada");
  const totalSaldo = sum("saldo_devedor_num");
  const totalFundo = sum("fundo_comum");
  const totalGarantia = sum("garantia");
  const totalTransfer = sum("transferencia");
  const totalSeguro = sum("seguro_vida");
  const prazoNums = selectedCotas.map((c) => parseInt(String(c.parcelas).replace(/\D/g, ""), 10) || 0);
  const prazoMedio = prazoNums.length ? Math.round(prazoNums.reduce((a, b) => a + b, 0) / prazoNums.length) : 0;
  const avalLabel = segmento === "veiculo" ? "Avaliação do bem" : "Avaliação do imóvel";
  const transferValue = totalTransfer > 0 ? money(totalTransfer) : "Consultar";

  // Parcelamento escalonado: cada cota paga só até o seu prazo.
  // Para o mês m, a parcela é a soma das parcelas das cotas cujo prazo >= m.
  const parcelamentoTiers = (() => {
    const arr = selectedCotas
      .map((c) => ({ prazo: parseInt(String(c.parcelas).replace(/\D/g, ""), 10) || 0, parcela: c.vlr_parcela_num || 0 }))
      .filter((x) => x.prazo > 0);
    if (arr.length === 0) return [];
    const distinct = [...new Set(arr.map((a) => a.prazo))].sort((a, b) => a - b);
    const tiers = [];
    let start = 1;
    for (const d of distinct) {
      const valor = arr.filter((a) => a.prazo >= d).reduce((s, a) => s + a.parcela, 0);
      tiers.push({ from: start, to: d, valor });
      start = d + 1;
    }
    return tiers;
  })();

  const buildSumText = () => {
    const lines = [
      `Segmento: ${selectedCotas[0]?.categoria || "-"}`,
      `Administradora: ${selectedAdmin || "-"}`,
      `Crédito: ${money(totalCredito)}`,
      `Entrada: ${money(totalEntrada)}`,
      `Prazo médio: ${prazoMedio} meses`,
      ``,
      `Parcelamento:`,
      ...parcelamentoTiers.map((t) => `${t.from} à ${t.to}: ${money(t.valor)}`),
      ``,
      `Saldo devedor: ${money(totalSaldo)}`,
      `Fundo comum: ${money(totalFundo)}`,
      `${avalLabel}: ${money(totalGarantia)}`,
      `Transferência: ${transferValue}`,
      `Seguro de vida: ${money(totalSeguro)}`,
      ``,
      `Cotas selecionadas:`,
      ...selectedCotas.map((c) => `Cód: ${c.codigo} - Crédito: ${c.credito_fmt}`),
    ];
    return lines.join("\n");
  };

  const copySumText = async () => {
    const text = buildSumText();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success("Informações copiadas!");
        return;
      }
      throw new Error("clipboard-unavailable");
    } catch (e) {
      // Fallback for browsers/contexts where the async Clipboard API is blocked
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        ta.setAttribute("readonly", "");
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Informações copiadas!");
        else toast.error("Não foi possível copiar.");
      } catch (e2) {
        toast.error("Não foi possível copiar.");
      }
    }
  };

  const exportExcel = () => {
    const headers = ["Codigo", "Categoria", "Credito", "Entrada", "Parcelas", "Vlr Parcela", "Administradora", "Status"];
    const rows = filtered.map((c) => [c.codigo, c.categoria, c.credito_fmt, c.entrada_fmt, c.parcelas, c.vlr_parcela, c.administradora, c.status]);
    const csv = [headers, ...rows].map((r) => r.map((x) => `"${x}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `cartas-${segmento}.csv`;
    a.click();
    toast.success("Planilha exportada!");
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: "Bellani Consórcios", url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado para a área de transferência!");
      }
    } catch (e) {}
  };

  const openSomar = () => {
    if (selectedCotas.length === 0) {
      toast.error("Selecione ao menos uma cota para somar.");
      return;
    }
    setSomarOpen(true);
  };

  return (
    <>
      <main className="max-w-6xl mx-auto px-4 md:px-5 pt-10 pb-44 fade-in">
      <h1 className="text-center text-3xl md:text-4xl font-extrabold text-slate-600 tracking-tight">
        {TITLES[segmento]}
      </h1>
      <div className="mx-auto mt-3 h-1 w-24 rounded-full gold-underline" />

      {/* export buttons */}
      <div className="flex items-center justify-center gap-6 mt-5">
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#1976D2] transition-colors">
          <FileText size={16} /> Baixar PDF
        </button>
        <button onClick={exportExcel} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#1976D2] transition-colors">
          <FileSpreadsheet size={16} /> Baixar Excel
        </button>
      </div>

      {/* search + sort */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código (ex.: 10424-8)"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:border-[#C9A227] focus:ring-2 focus:ring-[#C9A227]/25 outline-none text-sm text-slate-700"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="sm:w-[240px]">
            <div className="flex items-center gap-2">
              <ArrowUpDown size={16} className="text-slate-400" />
              <SelectValue placeholder="Ordenar" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="credito_desc">Crédito (maior primeiro)</SelectItem>
            <SelectItem value="credito_asc">Crédito (menor primeiro)</SelectItem>
            <SelectItem value="entrada_desc">Entrada (maior primeiro)</SelectItem>
            <SelectItem value="entrada_asc">Entrada (menor primeiro)</SelectItem>
            <SelectItem value="parcelas_desc">Nº parcelas (maior)</SelectItem>
            <SelectItem value="parcelas_asc">Nº parcelas (menor)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between mt-4 mb-4">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
          <span className="text-sm font-semibold text-[#1976D2]">Selecionar todas</span>
        </label>

        <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
          <SheetTrigger asChild>
            <button className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A227] hover:opacity-80 transition-opacity">
              <Filter size={16} /> Criar filtro
            </button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="text-slate-700">Filtrar cotas</SheetTitle>
            </SheetHeader>
            <div className="py-6 space-y-8">
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">Administradora</label>
                <Select value={admFilter} onValueChange={setAdmFilter}>
                  <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {admins.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-3 block">Faixa de crédito</label>
                <Slider value={range} min={0} max={maxCredito} step={5000} onValueChange={setRange} />
                <div className="flex justify-between mt-3 text-xs font-medium text-slate-500">
                  <span>{money(range[0])}</span>
                  <span>{money(range[1])}</span>
                </div>
              </div>
            </div>
            <SheetFooter>
              <button
                onClick={() => { setAdmFilter("all"); setRange([0, maxCredito]); }}
                className="w-full py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-semibold transition-colors"
              >
                Limpar filtros
              </button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      {/* cards */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400">
            <Loader2 className="animate-spin mb-3 text-[#C9A227]" size={32} />
            <span className="text-sm font-medium">Carregando cotas...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400 font-medium">Nenhuma cota encontrada com os filtros aplicados.</div>
        ) : (
          filtered.map((c) => (
            <CotaCard key={c.codigo} cota={c} selected={!!selected[c.codigo]} onToggle={toggle} onDetails={setDetail} destaque={opportunityCodes.has(c.codigo)} />
          ))
        )}
      </div>

      {/* disclaimer */}
      <div className="mt-10 border border-slate-200 rounded-2xl p-6 md:p-8 bg-slate-50/40">
        <div className="flex justify-center mb-4">
          <AlertTriangle className="text-[#C9A227]" size={40} />
        </div>
        <p className="text-center text-[13px] leading-relaxed text-slate-500 max-w-3xl mx-auto">
          A <b>Bellani Consórcios</b> reserva o direito de corrigir informações divulgadas em nosso site, incluindo detalhes sobre planos, prazos, valores de créditos, taxas, e quaisquer outras condições relacionadas à venda de cotas de consórcio, devido a erros de digitação ou imprecisões técnicas. Todas as informações e condições aqui apresentadas devem ser OBRIGATORIAMENTE validadas e confirmadas com o nosso Setor Comercial antes de qualquer contratação.
        </p>
        <div className="my-5 border-t border-slate-200" />
        <p className="text-center text-[13px] leading-relaxed text-slate-500 max-w-3xl mx-auto">
          A aquisição e transferência das cotas estão sujeitas à análise e aprovação da administradora, bem como às condições contratuais aplicáveis. Os valores e a disponibilidade das cotas podem sofrer alterações. Consulte as condições atualizadas antes da contratação.
        </p>
      </div>
      </main>

      {/* floating running summary that follows on screen */}
      {selectedCotas.length > 0 && (
        <div className="fixed bottom-[72px] left-0 right-0 z-40 px-4 fade-in">
          <div className="max-w-6xl mx-auto bg-white rounded-2xl border-2 border-[#C9A227] shadow-[0_10px_30px_-10px_rgba(201,162,39,.5)] p-3 md:p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="h-7 min-w-7 px-2 rounded-full bg-[#C9A227] text-white text-sm font-bold flex items-center justify-center">
                  {selectedCotas.length}
                </span>
                <span className="text-sm font-semibold text-slate-600">
                  cota(s) · <span className="text-slate-500 font-medium">{selectedAdmin}</span>
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">Crédito: <b className="text-[#1976D2]">{money(totalCredito)}</b></span>
                <span className="text-slate-500">Entrada: <b className="text-[#C9A227]">{money(totalEntrada)}</b></span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <button onClick={clearSelection} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 text-xs font-semibold transition-colors">
                  <X size={14} /> Limpar
                </button>
                <button onClick={openSomar} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#1976D2] hover:bg-[#1565c0] text-white text-sm font-bold transition-colors shadow-sm">
                  <Calculator size={16} /> Ver resultado
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-4px_20px_-8px_rgba(0,0,0,.15)]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={share} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-bold transition-colors">
            <Share2 size={17} /> Compartilhar
          </button>
          <button onClick={openSomar} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-[#1976D2] hover:bg-[#1565c0] text-white text-sm font-bold transition-colors shadow-sm">
            <Calculator size={17} /> Somar
            {selectedCotas.length > 0 && (
              <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-[#C9A227] text-white text-xs flex items-center justify-center">
                {selectedCotas.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Somar modal — "Resultado da junção" */}
      <Dialog open={somarOpen} onOpenChange={setSomarOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <DialogTitle className="text-slate-700 flex items-center gap-2">
                <Calculator className="text-[#1976D2]" size={20} /> Resultado da junção
              </DialogTitle>
              <button onClick={copySumText} title="Copiar informações" className="text-slate-400 hover:text-[#C9A227] transition-colors">
                <Copy size={18} />
              </button>
            </div>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto pr-1 text-sm">
            <div className="space-y-1.5">
              <Row label="Segmento" value={selectedCotas[0]?.categoria || "-"} bold />
              <Row label="Administradora" value={selectedAdmin || "-"} bold />
              <Row label="Crédito" value={money(totalCredito)} accent="#1976D2" />
              <Row label="Entrada" value={money(totalEntrada)} accent="#C9A227" />
              <Row label="Prazo médio" value={`${prazoMedio} meses`} />
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-slate-400 text-xs mb-1">Parcelamento:</div>
              {parcelamentoTiers.map((t, i) => (
                <div key={i} className="font-semibold text-slate-700">
                  {t.from} à {t.to}: {money(t.valor)}
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
              <Row label="Saldo devedor" value={money(totalSaldo)} />
              <Row label="Fundo comum" value={money(totalFundo)} />
              <Row label={avalLabel} value={money(totalGarantia)} />
              <Row label="Transferência" value={transferValue} />
              <Row label="Seguro de vida" value={money(totalSeguro)} />
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="font-bold text-slate-700 mb-2">Cotas selecionadas:</div>
              <div className="space-y-1.5">
                {selectedCotas.map((c) => (
                  <div key={c.codigo} className="flex justify-between text-xs text-slate-500">
                    <span>Cód {c.codigo}</span>
                    <span>{c.credito_fmt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button onClick={copySumText} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-bold transition-colors">
              <Copy size={16} /> Copiar
            </button>
            <a
              href={`https://wa.me/5541984584456?text=${encodeURIComponent(buildSumText())}`}
              target="_blank" rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-[#25a55b] hover:bg-[#1f9350] text-white text-sm font-bold transition-colors"
            >
              Negociar
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* Details modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-slate-700">Detalhes da cota {detail.codigo}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[
                  ["Categoria", detail.categoria],
                  ["Administradora", detail.administradora],
                  ["Crédito", detail.credito_fmt],
                  ["Entrada", detail.entrada_fmt],
                  ["Nº Parcelas", detail.parcelas],
                  ["Vlr Parcela", detail.vlr_parcela],
                  ["Saldo devedor", detail.saldo_devedor],
                  ["Fundo comum", detail.fundo_comum_fmt],
                  [segmento === "veiculo" ? "Avaliação do bem" : "Avaliação do imóvel", detail.garantia_fmt],
                  ["Transferência", detail.transferencia_fmt],
                  ["Seguro de vida", detail.seguro_vida_fmt],
                  ["Status", detail.status],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-xs text-slate-400">{k}</div>
                    <div className="font-semibold text-slate-700">{v ?? "—"}</div>
                  </div>
                ))}
              </div>
              <a href={buildWhatsAppLink(detail)} target="_blank" rel="noopener noreferrer"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-[#25a55b] hover:bg-[#1f9350] text-white text-sm font-bold transition-colors">
                Negociar no WhatsApp
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
